<template>
  <div class="tw-scaled-choice-element !m-0 flex flex-col gap-16 md:!mx-8 md:!mb-8 md:!mt-0 md:max-w-[420px]">
    <div class="inline-flex justify-center">
      <div class="tw-scaled-choice-group">
        <div class="tw-scaled-choice-group__buttons">
          <button
            v-for="(option, index) in options"
            :key="option?.id || index"
            type="button"
            class="size-[40px] p-[4px] text-[20px] md:size-[60px] md:text-[28px]"
            :style="generateOptionSpecificStyle(option)"
            :class="{
              selected: getSelected(option),
              'cursor-default': props.isEditorMode,
            }"
            @[btnClick]="handleOptionClick(option)"
          >
            <template v-if="config.type === ScaledChoiceType.FACE">
              <FaceEmojiSet :index="getEmojiIndex(options.length)[index]" />
            </template>
            <template v-else-if="config.type === ScaledChoiceType.THUMBS">
              <ThumbsEmojiSet :index="getEmojiIndex(options.length)[index]" />
            </template>
            <template v-else-if="config.type === ScaledChoiceType.CUSTOM">
              <img
                v-if="option.image"
                :src="option.image"
                class="max-h-full max-w-full"
                :alt="`option-${option.name}`"
              />
              <EmptyImage v-else />
            </template>
            <template v-else>
              {{ option.name }}
            </template>
          </button>
        </div>
        <div v-if="isAnchorVisible" class="tw-scaled-choice-group__anchor">
          <span class="text-left">{{ leftAnchorText }}</span>
          <span class="text-right">{{ rightAnchorText }}</span>
        </div>
      </div>
    </div>

    <div v-if="$slots.default && !isAnswerRequired" class="tw-scaled-choice-element__bottom mx-auto">
      <div
        class="tw-slot-wrapper min-w-[280px]"
        @[btnClick]="finish"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type PropType, ref, computed, onBeforeMount } from 'vue';
import type { Element as ElementType, PageOptions, QuestionOption } from '../../types/model';
import type { ElementOptions } from '../../types/options';
import FaceEmojiSet from './components/face-emoji-set.vue';
import ThumbsEmojiSet from './components/thumbs-emoji-set.vue';
import EmptyImage from './components/empty-image.vue';
import { ScaledChoiceType } from '@shared/types/options';
import { globalEmit } from '@shared/utils/helpers';
import { type QuizAnswer, getPreviousAnswerByQuestionType } from '@shared/utils/getPreviousAnswerByQuestionType';

const props = defineProps({
  config: { type: Object as PropType<ElementOptions['scaledchoice']>, default: () => ({}) },
  options: { type: Array as PropType<QuestionOption[]>, default: () => [] },
  pageOptions: { type: Object as PropType<PageOptions>, default: () => ({}) },
  isEditorMode: { type: Boolean, default: false },
  locale: { type: String, default: '' },
  element: { type: Object as PropType<ElementType<'scaledchoice'>>, default: () => ({}) },
  pageData: { type: Object, default: () => ({}) }
});

const selected = ref<QuestionOption[]>([]);
const btnClick = computed(() => (props.isEditorMode ? '' : 'click'));

const isAnswerRequired = computed(() => props.config?.required);

const values = computed(() => JSON.parse(props.element.values || '{}'));
const leftAnchorText = computed(() => values.value?.leftAnchor || '');
const rightAnchorText = computed(() => values.value?.rightAnchor || '');
const isAnchorVisible = computed(() => (leftAnchorText.value || rightAnchorText.value) && props.config?.anchor?.show);

const optionStyle = computed(() => {
  const fontFamily = props.config.buttons?.fontFamily || props.pageOptions.fontFamily;

  const fontSize = `${props.config.buttons?.fontSize || 14}px`;

  const color = {
    regular: props.config.buttons?.color?.regular || props.pageOptions.colors.text[0],
    hover: props.config.buttons?.color?.hover || props.pageOptions.colors.text[0],
    selected: props.config.buttons?.color?.selected || props.pageOptions.colors.text[0],
  }

  const background = {
    regular: props.config.buttons?.background?.regular || '#FFFFFF',
    hover: props.config.buttons?.background?.hover || '#FFFFFF',
    selected: props.config.buttons?.background?.selected || props.pageOptions.colors.theme[2],
  }

  const border = {
    regular: props.config.buttons?.border?.regular || props.pageOptions.colors.text[1],
    hover: props.config.buttons?.border?.hover || props.pageOptions.colors.theme[0],
    selected: props.config.buttons?.border?.selected || props.pageOptions.colors.theme[0],
    width: `${props.config.buttons?.border?.width || 0}px`,
  }

  const textStyle = {
    bold: props.config.buttons?.textStyle?.bold ? 'bold' : '',
    italic: props.config.buttons?.textStyle?.italic ? 'italic' : '',
    underline: props.config.buttons?.textStyle?.underline ? 'underline' : '',
    strikeThrough: props.config.buttons?.textStyle?.strikeThrough ? 'line-through' : ''
  }

  const textDecoration = `${textStyle.underline} ${textStyle.strikeThrough}`.trim();

  const anchorOrder = props.config.anchor?.position === 'top' ? -1 : 0;

  return {
    fontFamily,
    fontSize,
    color,
    background,
    border,
    textStyle,
    textDecoration,
    anchorOrder
  }
});

function getEmojiIndex(buttonCount:number) {
  const mappings = {
    2: [1, 7],
    3: [1, 4, 7],
    4: [1, 3, 5, 7],
    5: [1, 3, 4, 5, 7],
    6: [1, 2, 3, 5, 6, 7],
    7: [1, 2, 3, 4, 5, 6, 7]
  } as Record<number, number[]>;
  return mappings[buttonCount] || [];
}

function hasOptionSettingKey(option: QuestionOption, settingKey: keyof QuestionOption) {
  return Object.keys(option).includes(settingKey);
}

function generateOptionSpecificStyle(option: QuestionOption) {
  const isCustomizeBgColor = hasOptionSettingKey(option, 'customizeBgColor') && option.customizeBgColor;
  return {
    ...(isCustomizeBgColor && { backgroundColor: option.bgColor?.regular || optionStyle.value.background.regular }),
  }
}

function getSelected(option: QuestionOption) {
  return selected.value.find((item) => item.name === option.name);
}

function selectOption(option: QuestionOption) {
  const isSelected = getSelected(option);
  if (isSelected && !isAnswerRequired.value) {
    selected.value = [];
    return;
  }
  selected.value = [option];
}

function finish(event?: any) {
  globalEmit('nextQuestion', [event, selected.value]);
}

function handleOptionClick(option: QuestionOption) {
  selectOption(option);
  if (isAnswerRequired.value) finish();
}

function setPreviousAnswer() {
  if (props.pageData?.answers?.length) {
    selected.value = getPreviousAnswerByQuestionType({
      answers: props.pageData?.answers as QuizAnswer[],
      questionType: 'scaledchoice',
      questionOptions: props.options
    });
  }
}

onBeforeMount(setPreviousAnswer)
</script>

<style lang="postcss" scoped>
.tw-scaled-choice-group {
  --fontFamily: v-bind('optionStyle.fontFamily');
  --fontSize: v-bind('optionStyle.fontSize');
  --colorRegular: v-bind('optionStyle.color.regular');
  --colorHover: v-bind('optionStyle.color.hover');
  --colorSelected: v-bind('optionStyle.color.selected');
  --borderColorRegular: v-bind('optionStyle.border?.regular');
  --borderColorHover: v-bind('optionStyle.border?.hover');
  --borderColorSelected: v-bind('optionStyle.border?.selected');
  --borderWidth: v-bind('optionStyle.border.width');
  --backgroundRegular: v-bind('optionStyle.background.regular');
  --backgroundHover: v-bind('optionStyle.background.hover');
  --backgroundSelected: v-bind('optionStyle.background.selected');
  --bold: v-bind('optionStyle.textStyle.bold');
  --italic: v-bind('optionStyle.textStyle.italic');
  --textDecoration: v-bind('optionStyle.textDecoration');
  --anchorOrder: v-bind('optionStyle.anchorOrder');

  @apply flex flex-col gap-4;
  font-family: var(--fontFamily);

  &__buttons {
    @apply flex justify-center;
    button {
      @apply relative border-solid inline-flex shrink-0 font-[600] justify-center items-center;
      border-width: var(--borderWidth);
      border-color: var(--borderColorRegular);
      background-color: var(--backgroundRegular);
      margin-left: calc(var(--borderWidth) * -1);
      &:hover {
        border-color: var(--borderColorHover);
        background-color: var(--backgroundHover);
        z-index: 11;
      }
      &.selected {
        border-color: var(--borderColorSelected);
        background-color: var(--backgroundSelected);
        z-index: 10;
      }
    }
  }

  &__anchor {
    @apply flex justify-between gap-4;
    line-height: 1.33;
    word-break: break-word;
    order: var(--anchorOrder);
    font-size: var(--fontSize);
    font-weight: var(--bold);
    font-style: var(--italic);
    color: var(--colorRegular);
    text-decoration: var(--textDecoration);
  }
}
</style>
